import { SDK } from "@axl-dev/school-sdk";
import { Modal, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoc } from "@app/shared/lib/hooks";
import { generateBtnHoverCss, parseBtnHoverProps, BtnHoverProps } from "@app/widgets/shared/btnHoverCss";

type StudentPaymentMethodItem = {
  id: string;
  createdDate: string;
  cardFirstSixDigits: string | null;
  cardLastFourDigits: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
};

export type PaymentMethodListWidgetProps = BtnHoverProps & {
  sdk: SDK;
  layout: "grid" | "list";
  hideWhenEmpty: boolean;
  cardGap: string;
  cardWidth: string;
  cardBorderRadius: string;
  cardBgColor: string;
  cardBorderColor: string;
  cardPadding: string;
  titleText: string;
  titleFontSize: string;
  titleFontFamily: string;
  titleFontWeight: string;
  titleTextColor: string;
  titleTextAlign: string;
  hintText: string;
  hintTextColor: string;
  hintFontSize: string;
  showBrand: boolean;
  brandColor: string;
  brandFontSize: string;
  brandFontWeight: string;
  numberColor: string;
  numberFontSize: string;
  showExpiration: boolean;
  expirationColor: string;
  expirationFontSize: string;
  showExpiredBadge: boolean;
  showDetachBtn: boolean;
  detachBtnText: string;
  detachBtnTextColor: string;
  detachBtnBorderRadius: string;
  detachBtnFontSize: string;
  emptyText: string;
  emptyTextColor: string;
  emptyTextFontSize: string;
};

export const parsePaymentMethodListProps = (container: HTMLElement): Omit<PaymentMethodListWidgetProps, "sdk"> => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;

  return {
    layout: attr("data-layout", "grid") as "grid" | "list",
    hideWhenEmpty: attr("data-hide-when-empty", "true") === "true",
    cardGap: attr("data-card-gap", "16px"),
    cardWidth: attr("data-card-width", "300px"),
    cardBorderRadius: attr("data-card-border-radius", "8px"),
    cardBgColor: attr("data-card-bg-color", "#ffffff"),
    cardBorderColor: attr("data-card-border-color", "#f0f0f0"),
    cardPadding: attr("data-card-padding", "12px"),
    titleText: attr("data-title"),
    titleFontSize: attr("data-title-font-size", "20px"),
    titleFontFamily: attr("data-title-font-family"),
    titleFontWeight: attr("data-title-font-weight", "600"),
    titleTextColor: attr("data-title-text-color"),
    titleTextAlign: attr("data-title-text-align", "left"),
    hintText: attr("data-hint"),
    hintTextColor: attr("data-hint-text-color", "#888888"),
    hintFontSize: attr("data-hint-font-size", "13px"),
    showBrand: attr("data-show-brand", "true") === "true",
    brandColor: attr("data-brand-color", "#333333"),
    brandFontSize: attr("data-brand-font-size", "14px"),
    brandFontWeight: attr("data-brand-font-weight", "500"),
    numberColor: attr("data-number-color", "#888888"),
    numberFontSize: attr("data-number-font-size", "13px"),
    showExpiration: attr("data-show-expiration", "true") === "true",
    expirationColor: attr("data-expiration-color", "#999999"),
    expirationFontSize: attr("data-expiration-font-size", "12px"),
    showExpiredBadge: attr("data-show-expired-badge", "true") === "true",
    showDetachBtn: attr("data-show-detach-btn", "true") === "true",
    detachBtnText: attr("data-detach-btn-text"),
    detachBtnTextColor: attr("data-detach-btn-text-color", "#ff4d4f"),
    detachBtnBorderRadius: attr("data-detach-btn-border-radius", "5px"),
    detachBtnFontSize: attr("data-detach-btn-font-size", "13px"),
    emptyText: attr("data-empty-text"),
    emptyTextColor: attr("data-empty-text-color", "#aaaaaa"),
    emptyTextFontSize: attr("data-empty-text-font-size", "14px"),
    ...parseBtnHoverProps(container),
  };
};

const getCardBrand = (firstSix: string | null): string => {
  const bin = firstSix ?? "";
  if (/^4/.test(bin)) return "Visa";
  if (/^220[0-4]/.test(bin)) return "МИР";
  const firstFour = Number(bin.substring(0, 4));
  if (/^5[1-5]/.test(bin) || (firstFour >= 2221 && firstFour <= 2720)) return "Mastercard";
  if (/^3[47]/.test(bin)) return "American Express";
  if (/^62/.test(bin)) return "UnionPay";
  return "";
};

const formatCardNumber = (item: StudentPaymentMethodItem): string => {
  if (!item.cardLastFourDigits) return "";

  const prefix = item.cardFirstSixDigits
    ? `${item.cardFirstSixDigits.substring(0, 4)} ${item.cardFirstSixDigits.substring(4)}•• ••••`
    : "•••• •••• ••••";

  return `${prefix} ${item.cardLastFourDigits}`;
};

const isExpired = (item: StudentPaymentMethodItem): boolean => {
  if (!item.cardExpMonth || !item.cardExpYear) return false;

  const now = new Date();
  const expiresAt = new Date(item.cardExpYear, item.cardExpMonth, 0, 23, 59, 59);
  return expiresAt < now;
};

export const PaymentMethodListWidget: React.FC<PaymentMethodListWidgetProps> = (props) => {
  const { sdk } = props;
  const loc = useLoc();

  const [items, setItems] = useState<StudentPaymentMethodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [detachingId, setDetachingId] = useState<string | null>(null);

  const scopeId = useMemo(() => `ez-pm-${Math.random().toString(36).slice(2, 8)}`, []);
  const hoverCss = useMemo(() => generateBtnHoverCss(`.${scopeId}`, "button", props),
    [scopeId, props.btnHoverEffect, props.btnHoverBgColor, props.btnHoverTextColor, props.btnHoverShadow, props.btnHoverScale, props.btnHoverTransitionDuration]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const result = await sdk.api.get<{ items: StudentPaymentMethodItem[] }>("/student/payment-method");
      if (result.success) setItems(result.response?.items ?? []);
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const detach = useCallback((item: StudentPaymentMethodItem) => {
    Modal.confirm({
      title: loc.word("PaymentMethod.detachConfirmMsg", {
        default: "This card will stop being charged and your subscriptions will not renew. Detach it?",
      }),
      okText: loc.word("PaymentMethod.detachMsg", { default: "Detach" }),
      okButtonProps: { danger: true },
      onOk: async () => {
        setDetachingId(item.id);
        try {
          await sdk.api.delete(`/student/payment-method/${item.id}`);
          await fetchItems();
        } finally {
          setDetachingId(null);
        }
      },
    });
  }, [sdk, loc, fetchItems]);

  if (loading) return <Spin />;

  if (items.length === 0) {
    if (props.hideWhenEmpty) return null;

    return (
      <div style={{ color: props.emptyTextColor, fontSize: props.emptyTextFontSize }}>
        {props.emptyText || loc.word("School.paymentMethodsEmptyMsg", { default: "No saved cards" })}
      </div>
    );
  }

  const isGrid = props.layout === "grid";

  return (
    <div className={scopeId}>
      <style>{hoverCss}</style>
      {props.titleText && (
        <div style={{
          fontSize: props.titleFontSize,
          fontWeight: props.titleFontWeight as any,
          fontFamily: props.titleFontFamily || undefined,
          color: props.titleTextColor || undefined,
          textAlign: props.titleTextAlign as any,
          marginBottom: "8px",
        }}>{props.titleText}</div>
      )}
      {props.hintText && (
        <div style={{ fontSize: props.hintFontSize, color: props.hintTextColor, marginBottom: "12px" }}>
          {props.hintText}
        </div>
      )}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: props.cardGap,
        ...(isGrid ? {} : { flexDirection: "column" as const }),
      }}>
        {items.map((item) => {
          const brand = getCardBrand(item.cardFirstSixDigits);
          const expired = isExpired(item);

          return (
            <div key={item.id} style={{
              width: isGrid ? props.cardWidth : "100%",
              maxWidth: isGrid ? props.cardWidth : "100%",
              border: `1px solid ${props.cardBorderColor}`,
              borderRadius: props.cardBorderRadius,
              backgroundColor: props.cardBgColor,
              padding: props.cardPadding,
              position: "relative",
            }}>
              {props.showBrand && (
                <div style={{
                  color: props.brandColor,
                  fontSize: props.brandFontSize,
                  fontWeight: props.brandFontWeight as any,
                }}>
                  {brand || loc.word("PaymentMethod.cardLabel", { default: "Card" })}
                </div>
              )}
              <div style={{ color: props.numberColor, fontSize: props.numberFontSize }}>
                {formatCardNumber(item)}
              </div>
              {props.showExpiration && item.cardExpMonth && item.cardExpYear && (
                <div style={{
                  color: expired && props.showExpiredBadge ? "#cf1322" : props.expirationColor,
                  fontSize: props.expirationFontSize,
                  marginTop: "2px",
                }}>
                  {loc.word("PaymentMethod.expiresLabel", { default: "Valid thru" })}{"\u00a0"}
                  {`${String(item.cardExpMonth).padStart(2, "0")}/${String(item.cardExpYear).slice(-2)}`}
                  {expired && props.showExpiredBadge &&
                    `\u00a0·\u00a0${loc.word("PaymentMethod.expiredShortLabel", { default: "expired" })}`}
                </div>
              )}
              {props.showDetachBtn && (
                <button type="button"
                  disabled={detachingId === item.id}
                  onClick={() => detach(item)}
                  title={props.detachBtnText || loc.word("PaymentMethod.detachMsg", { default: "Detach" })}
                  style={{
                    position: "absolute",
                    top: "6px",
                    right: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "26px",
                    height: "26px",
                    padding: 0,
                    border: "none",
                    borderRadius: props.detachBtnBorderRadius,
                    backgroundColor: "transparent",
                    color: props.detachBtnTextColor,
                    fontSize: props.detachBtnFontSize,
                    lineHeight: 1,
                    cursor: detachingId === item.id ? "wait" : "pointer",
                  }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
